import React from 'react';

import type * as api from '@gitbook/api-client';
import type { AnalyticsContextSpaceContent } from '@gitbook/app-analytics/client';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    BreadcrumbButton,
    BreadcrumbItem,
    BreadcrumbSpan,
    Breadcrumbs,
    Heading,
    SidePanel,
    SidePanelBody,
    SidePanelCloseButton,
    SidePanelHeader,
    SidePanelHeaderActions,
    SidePanelHeaderMain,
    SidePanelHeaderSecondary,
    SidePanelHeaderTitle,
} from '@gitbook/spine-react';

import { HiveBranchHistory } from '../../../Components';
import { useNavigation, useSetAnalyticsScreen } from '../../../navigation';

/**
 * Side panel showing the history of a space or change request.
 */
export function HistorySidePanel(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    screen: NavigationScreenSpaceState;
    analyticsContext: AnalyticsContextSpaceContent;
}): React.ReactElement | null {
    const { space, changeRequest, screen, analyticsContext } = props;

    const { navigate } = useNavigation();

    const activeRevisionId = screen.revision;
    const initialRevisionId = changeRequest ? changeRequest.revision : space.revision;

    useSetAnalyticsScreen({ ...analyticsContext, screen: 'space_activity' });

    const onClose = React.useCallback(() => {
        navigate({
            current: {
                revision: undefined,
                sideSheet: undefined,
                mode: undefined,
            },
        });
    }, [navigate]);

    return (
        <SidePanel onClose={onClose}>
            <SidePanelHeader>
                <SidePanelHeaderMain>
                    <SidePanelHeaderTitle>
                        <Heading>Version history</Heading>
                    </SidePanelHeaderTitle>
                    <SidePanelHeaderActions>
                        <SidePanelCloseButton />
                    </SidePanelHeaderActions>
                </SidePanelHeaderMain>
                {changeRequest ? (
                    <SidePanelHeaderSecondary>
                        <Breadcrumbs label="History breadcrumbs">
                            <BreadcrumbItem>
                                <BreadcrumbButton
                                    label="Space"
                                    isActive={false}
                                    onPress={() => {
                                        navigate({
                                            current: {
                                                changeRequest: undefined,
                                                revision: space.revision,
                                                sideSheet: undefined,
                                                mode: undefined,
                                            },
                                        });
                                    }}
                                />
                            </BreadcrumbItem>
                            <BreadcrumbItem>
                                <BreadcrumbSpan isCurrent icon={Icon.ChangeRequest}>
                                    Change request #{changeRequest?.number}
                                </BreadcrumbSpan>
                            </BreadcrumbItem>
                        </Breadcrumbs>
                    </SidePanelHeaderSecondary>
                ) : null}
            </SidePanelHeader>
            <SidePanelBody>
                <HiveBranchHistory
                    space={space}
                    changeRequest={changeRequest}
                    initialRevisionId={initialRevisionId}
                    activeRevisionId={activeRevisionId}
                    readOnly={!space.permissions.merge}
                />
            </SidePanelBody>
        </SidePanel>
    );
}
